import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  GenerationKind,
  GenerationStatus,
  type CourseStatus,
  type EpisodeGenerationStatus,
} from '@courseai/domain';
import { useAuth } from '../auth/AuthContext';
import { useT, type I18n, type TranslationKey } from '../i18n';
import {
  GET_PROJECT,
  type ProjectDetailData,
  type GetProjectVariables,
} from '../graphql/project-detail';
import {
  GET_COURSE,
  GET_MEDIA_URL,
  GENERATE_EPISODE_AUDIO,
  GENERATE_EPISODE_VIDEO,
  type GetCourseData,
  type GetCourseVariables,
  type EpisodeDetail,
  type GetMediaUrlData,
  type GetMediaUrlVariables,
  type GenerateEpisodeAudioData,
  type GenerateEpisodeAudioVariables,
  type GenerateEpisodeVideoData,
  type GenerateEpisodeVideoVariables,
} from '../graphql/courses';
import {
  GET_GENERATION_REQUEST,
  MY_GENERATION_REQUESTS,
  type GetGenerationRequestData,
  type GetGenerationRequestVariables,
  type MyGenerationRequestsData,
  type MyGenerationRequestsVariables,
} from '../graphql/generations';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// Translation keys for the course lifecycle status shown in the header badge.
const COURSE_STATUS_KEYS: Record<CourseStatus, TranslationKey> = {
  draft: 'courseStatus.draft',
  proposed: 'courseStatus.proposed',
  approved: 'courseStatus.approved',
  generating: 'courseStatus.generating',
  ready: 'courseStatus.ready',
};

// Translation keys for a per-kind episode generation status (audio/video).
const EPISODE_STATUS_KEYS: Record<EpisodeGenerationStatus, TranslationKey> = {
  none: 'episodeStatus.none',
  pending: 'episodeStatus.pending',
  running: 'episodeStatus.running',
  ready: 'episodeStatus.ready',
  failed: 'episodeStatus.failed',
};

function courseStatusLabel(t: I18n['t'], status: string): string {
  const key = COURSE_STATUS_KEYS[status as CourseStatus];
  return key === undefined ? status : t(key);
}

function episodeStatusLabel(t: I18n['t'], status: string): string {
  const key = EPISODE_STATUS_KEYS[status as EpisodeGenerationStatus];
  return key === undefined ? status : t(key);
}

// Course detail screen: renders a course and its episodes (ordered ascending by
// episodeIndex, Req 4.3). For each episode it shows the title, objective/script
// presence, proposed duration, and the audio/video generation statuses. Owners
// and editors can trigger audio/video generation (Req 7.1, 8.1); those
// mutations return a GenerationRequest and the live progress is tracked by the
// Generaciones tab. Episodes with stored media play via a presigned URL fetched
// on demand (Req 9.2).
export function CourseDetail(): ReactNode {
  const { projectId, courseId } = useParams<{ projectId: string; courseId: string }>();
  const auth = useAuth();
  const { t } = useT();

  const missingParams = projectId === undefined || courseId === undefined;

  // Resolve the caller's role from their project membership so we can gate the
  // generation controls to owners/editors (Req 7.1, 8.1). The server enforces
  // this too; the UI mirrors it for immediate feedback.
  const { data: projectData } = useQuery<ProjectDetailData, GetProjectVariables>(GET_PROJECT, {
    variables: { projectId: projectId ?? '' },
    skip: missingParams,
    fetchPolicy: 'cache-first',
  });
  const role = projectData?.getProject?.members.find(
    (member) => member.userId === auth.user?.userId,
  )?.role;
  const canEdit = role === 'owner' || role === 'editor';

  const { data, loading, error, refetch } = useQuery<GetCourseData, GetCourseVariables>(
    GET_COURSE,
    {
      variables: { projectId: projectId ?? '', courseId: courseId ?? '' },
      skip: missingParams,
      fetchPolicy: 'cache-and-network',
    },
  );

  // Load the project's generation requests so we can show a PERSISTENT failure
  // reason on an episode whose last audio/video generation FAILED — the
  // errorDescription lives on the request, not the episode, so without this the
  // episode only shows 'Failed' with no reason (and only while a live progress
  // poll is running). `refetchGenerations` is called after a generation
  // completes so a fresh failure/clearing shows immediately.
  const { data: generationsData, refetch: refetchGenerations } = useQuery<
    MyGenerationRequestsData,
    MyGenerationRequestsVariables
  >(MY_GENERATION_REQUESTS, {
    variables: { projectId: projectId ?? '' },
    skip: missingParams,
    fetchPolicy: 'cache-and-network',
  });

  // Latest FAILED request's errorDescription per (episodeIndex, kind) for THIS
  // course, so an episode can show why its last audio/video attempt failed.
  const failureByEpisodeKind = useMemo(() => {
    const map = new Map<string, string>();
    const requests = generationsData?.myGenerationRequests ?? [];
    // Newest first so the first FAILED we see per key is the latest.
    const sorted = [...requests].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    for (const req of sorted) {
      if (req.courseId !== courseId) continue;
      if (typeof req.episodeIndex !== 'number') continue;
      const kind =
        req.kind === GenerationKind.EPISODE_AUDIO
          ? 'audio'
          : req.kind === GenerationKind.EPISODE_VIDEO
            ? 'video'
            : undefined;
      if (kind === undefined) continue;
      const key = `${req.episodeIndex}:${kind}`;
      if (map.has(key)) continue; // keep only the latest per key
      if (req.status === GenerationStatus.FAILED) {
        map.set(key, req.errorDescription ?? '');
      } else {
        // A later success supersedes an older failure: record 'no failure'.
        map.set(key, '');
      }
    }
    return map;
  }, [generationsData, courseId]);

  if (missingParams) {
    return (
      <section className="screen">
        <p className="form-error">{t('course.notSpecified')}</p>
      </section>
    );
  }

  const course = data?.getCourse ?? undefined;

  return (
    <section className="screen">
      <p>
        <Link to={`/projects/${projectId}`}>{t('course.backToProject')}</Link>
      </p>

      {loading && data === undefined && <p>{t('course.loading')}</p>}

      {error && (
        <div>
          <p className="form-error">{t('course.loadError', { message: error.message })}</p>
          <button type="button" onClick={() => void refetch()}>
            {t('courses.retry')}
          </button>
        </div>
      )}

      {!loading && data !== undefined && course === undefined && (
        <p className="tab-placeholder">{t('course.notFound')}</p>
      )}

      {course && (
        <>
          <div className="course-detail-header">
            <div>
              <h2>{course.title}</h2>
              <p className="course-detail-topic">{course.topicPrompt}</p>
            </div>
            <span className={`course-status course-status-${course.status}`}>
              {courseStatusLabel(t, course.status)}
            </span>
          </div>

          <p className="course-detail-meta">
            {t('course.language', { language: course.language })}
          </p>

          <h3>{t('course.episodes')}</h3>
          {course.episodes.length === 0 ? (
            <p className="tab-placeholder">{t('course.noEpisodes')}</p>
          ) : (
            <ul className="episode-list">
              {course.episodes.map((episode) => (
                <EpisodeCard
                  key={episode.episodeIndex}
                  projectId={projectId}
                  courseId={courseId}
                  episode={episode}
                  canEdit={canEdit}
                  audioFailure={failureByEpisodeKind.get(`${episode.episodeIndex}:audio`)}
                  videoFailure={failureByEpisodeKind.get(`${episode.episodeIndex}:video`)}
                  onGenerated={async () => {
                    await refetch();
                    await refetchGenerations();
                  }}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

interface EpisodeCardProps {
  projectId: string;
  courseId: string;
  episode: EpisodeDetail;
  canEdit: boolean;
  /**
   * The last audio/video generation's failure reason for this episode, when its
   * most recent attempt FAILED. An empty string or undefined means "no known
   * failure" (either it succeeded or there's no record). Surfaced persistently
   * so the creator can see WHY it failed without an active progress poll.
   */
  audioFailure?: string | undefined;
  videoFailure?: string | undefined;
  /** Called when a generation reaches READY so the course (and media) refreshes. */
  onGenerated(): Promise<void> | void;
}

// A single episode row: title, script presence, proposed duration, the
// audio/video generation statuses, generation controls (owner/editor), and
// on-demand media players for episodes that already have stored audio/video.
//
// Generating audio or video shows a live progress bar + one-line status
// (Queued -> Generating -> Ready / Failed) driven by polling the returned
// GenerationRequest, and refreshes the course when a generation completes so
// the new media player appears.
function EpisodeCard({
  projectId,
  courseId,
  episode,
  canEdit,
  audioFailure,
  videoFailure,
  onGenerated,
}: EpisodeCardProps): ReactNode {
  const { t } = useT();
  const { episodeIndex, title, script, proposedDurationSeconds, audioStatus, videoStatus } =
    episode;

  const hasScript = typeof script === 'string' && script.trim().length > 0;

  const [actionError, setActionError] = useState<string | undefined>(undefined);
  // The in-flight generation request id per kind, so each shows its own bar.
  const [audioRequestId, setAudioRequestId] = useState<string | undefined>(undefined);
  const [videoRequestId, setVideoRequestId] = useState<string | undefined>(undefined);

  const [generateAudio, { loading: audioStarting }] = useMutation<
    GenerateEpisodeAudioData,
    GenerateEpisodeAudioVariables
  >(GENERATE_EPISODE_AUDIO);
  const [generateVideo, { loading: videoStarting }] = useMutation<
    GenerateEpisodeVideoData,
    GenerateEpisodeVideoVariables
  >(GENERATE_EPISODE_VIDEO);

  async function handleGenerateAudio(): Promise<void> {
    setActionError(undefined);
    setAudioRequestId(undefined);
    try {
      const result = await generateAudio({
        variables: { input: { projectId, courseId, episodeIndex } },
      });
      const id = result.data?.generateEpisodeAudio.generationRequestId;
      if (id === undefined) {
        setActionError(t('course.audioStartError'));
        return;
      }
      setAudioRequestId(id);
    } catch (err) {
      setActionError(errorMessage(err, t('course.audioStartError')));
    }
  }

  async function handleGenerateVideo(): Promise<void> {
    setActionError(undefined);
    setVideoRequestId(undefined);
    try {
      const result = await generateVideo({
        variables: { input: { projectId, courseId, episodeIndex } },
      });
      const id = result.data?.generateEpisodeVideo.generationRequestId;
      if (id === undefined) {
        setActionError(t('course.videoStartError'));
        return;
      }
      setVideoRequestId(id);
    } catch (err) {
      setActionError(errorMessage(err, t('course.videoStartError')));
    }
  }

  const audioBusy = audioStarting || audioRequestId !== undefined;
  const videoBusy = videoStarting || videoRequestId !== undefined;

  return (
    <li className="episode-card">
      <div className="episode-card-header">
        <span className="episode-card-index">
          {t('course.episodeIndex', { index: episodeIndex + 1 })}
        </span>
        <span className="episode-card-title">{title}</span>
      </div>

      <dl className="episode-card-fields">
        <div>
          <dt>{t('course.field.script')}</dt>
          <dd>{hasScript ? t('course.field.scriptAvailable') : t('course.field.scriptMissing')}</dd>
        </div>
        <div>
          <dt>{t('course.field.proposedDuration')}</dt>
          <dd>
            {typeof proposedDurationSeconds === 'number'
              ? t('course.field.durationSeconds', { seconds: proposedDurationSeconds })
              : t('course.field.durationUndefined')}
          </dd>
        </div>
        <div>
          <dt>{t('course.field.audio')}</dt>
          <dd>
            {episodeStatusLabel(t, audioStatus)}
            {audioStatus === 'failed' && audioFailure !== undefined && audioFailure.length > 0 && (
              <span className="episode-failure-reason">{audioFailure}</span>
            )}
          </dd>
        </div>
        <div>
          <dt>{t('course.field.video')}</dt>
          <dd>
            {episodeStatusLabel(t, videoStatus)}
            {videoStatus === 'failed' && videoFailure !== undefined && videoFailure.length > 0 && (
              <span className="episode-failure-reason">{videoFailure}</span>
            )}
          </dd>
        </div>
      </dl>

      {hasScript && (
        <details className="episode-card-script">
          <summary>{t('course.viewScript')}</summary>
          <p className="episode-card-script-text">{script}</p>
        </details>
      )}

      {canEdit && (
        <div className="episode-card-actions">
          <button type="button" onClick={() => void handleGenerateAudio()} disabled={audioBusy}>
            {audioBusy ? t('course.starting') : t('course.generateAudio')}
          </button>
          <button type="button" onClick={() => void handleGenerateVideo()} disabled={videoBusy}>
            {videoBusy ? t('course.starting') : t('course.generateVideo')}
          </button>
        </div>
      )}

      {audioRequestId !== undefined && (
        <GenerationProgress
          projectId={projectId}
          generationRequestId={audioRequestId}
          kind="audio"
          onDone={async () => {
            setAudioRequestId(undefined);
            await onGenerated();
          }}
        />
      )}
      {videoRequestId !== undefined && (
        <GenerationProgress
          projectId={projectId}
          generationRequestId={videoRequestId}
          kind="video"
          onDone={async () => {
            setVideoRequestId(undefined);
            await onGenerated();
          }}
        />
      )}

      {actionError && <p className="form-error">{actionError}</p>}

      <div className="episode-card-media">
        {typeof episode.audioObjectKey === 'string' && episode.audioObjectKey.length > 0 && (
          <MediaPlayer
            projectId={projectId}
            objectKey={episode.audioObjectKey}
            kind="audio"
            label={t('course.playAudio')}
          />
        )}
        {typeof episode.videoObjectKey === 'string' && episode.videoObjectKey.length > 0 && (
          <MediaPlayer
            projectId={projectId}
            objectKey={episode.videoObjectKey}
            kind="video"
            label={t('course.playVideo')}
          />
        )}
      </div>
    </li>
  );
}

interface GenerationProgressProps {
  projectId: string;
  generationRequestId: string;
  kind: 'audio' | 'video';
  /** Called once the generation reaches a terminal state (READY or FAILED). */
  onDone(): Promise<void> | void;
}

// How often to poll the generation request while it is in flight.
const GENERATION_POLL_INTERVAL_MS = 4000;

// A live progress indicator for one in-flight episode generation: an animated
// (indeterminate) bar while the request is PENDING/RUNNING, with a one-line
// status message underneath that updates through the lifecycle. Polls
// getGenerationRequest until the request reaches READY (success) or FAILED, then
// shows the terminal line and calls onDone so the course refreshes.
function GenerationProgress({
  projectId,
  generationRequestId,
  kind,
  onDone,
}: GenerationProgressProps): ReactNode {
  const client = useApolloClient();
  const { t } = useT();
  const [status, setStatus] = useState<GenerationStatus>(GenerationStatus.PENDING);
  const [errorDescription, setErrorDescription] = useState<string | undefined>(undefined);

  // Keep the latest onDone without retriggering the poll effect.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const kindLabel = kind === 'audio' ? t('course.field.audio') : t('course.field.video');

  useEffect(() => {
    let cancelled = false;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const result = await client.query<
            GetGenerationRequestData,
            GetGenerationRequestVariables
          >({
            query: GET_GENERATION_REQUEST,
            variables: { projectId, generationRequestId },
            fetchPolicy: 'network-only',
          });
          if (cancelled) {
            return;
          }
          const req = result.data.getGenerationRequest;
          if (req === null) {
            return;
          }
          setStatus(req.status as GenerationStatus);
          if (req.status === GenerationStatus.READY || req.status === GenerationStatus.FAILED) {
            if (req.status === GenerationStatus.FAILED) {
              setErrorDescription(req.errorDescription ?? undefined);
            }
            clearInterval(timer);
            await onDoneRef.current();
          }
        } catch {
          // Transient poll error — keep trying until a terminal state.
        }
      })();
    }, GENERATION_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [client, projectId, generationRequestId]);

  const inProgress = status === GenerationStatus.PENDING || status === GenerationStatus.RUNNING;
  const failed = status === GenerationStatus.FAILED;
  const ready = status === GenerationStatus.READY;

  // The one-line status message.
  const message = failed
    ? t('course.generationFailed', {
        kind: kindLabel,
        message: errorDescription ?? t('course.generationFailedNoDetail'),
      })
    : ready
      ? t('course.generationReady', { kind: kindLabel })
      : status === GenerationStatus.RUNNING
        ? t('course.generationRunning', { kind: kindLabel })
        : t('course.generationQueued', { kind: kindLabel });

  return (
    <div className={`generation-progress generation-progress-${status.toLowerCase()}`}>
      {inProgress && (
        <div
          className="generation-progress-bar"
          role="progressbar"
          aria-label={t('course.generationRunning', { kind: kindLabel })}
        >
          <span className="generation-progress-fill" />
        </div>
      )}
      <p className={`generation-progress-status${failed ? ' form-error' : ''}`}>{message}</p>
    </div>
  );
}

interface MediaPlayerProps {
  projectId: string;
  objectKey: string;
  kind: 'audio' | 'video';
  label: string;
}

// On-demand media playback for a stored object. The presigned URL is
// time-limited (Req 9.2, 9.5), so it is fetched lazily when the creator asks to
// play — not on page load — ensuring a fresh, unexpired URL sources the
// <audio>/<video> element. `getMediaUrl` runs with `network-only` so each play
// re-fetches rather than reusing a possibly stale cached URL.
function MediaPlayer({ projectId, objectKey, kind, label }: MediaPlayerProps): ReactNode {
  const client = useApolloClient();
  const { t } = useT();
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);

  async function loadUrl(): Promise<void> {
    setLoading(true);
    setLoadError(undefined);
    try {
      const result = await client.query<GetMediaUrlData, GetMediaUrlVariables>({
        query: GET_MEDIA_URL,
        variables: { input: { projectId, objectKey } },
        fetchPolicy: 'network-only',
      });
      setUrl(result.data.getMediaUrl.url);
    } catch (err) {
      setLoadError(errorMessage(err, t('course.mediaUrlError')));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="media-player">
      {url === undefined ? (
        <button type="button" onClick={() => void loadUrl()} disabled={loading}>
          {loading ? t('course.mediaLoading') : label}
        </button>
      ) : kind === 'audio' ? (
        <audio controls src={url} className="media-player-audio">
          {t('course.audioUnsupported')}
        </audio>
      ) : (
        <video controls src={url} className="media-player-video">
          {t('course.videoUnsupported')}
        </video>
      )}
      {loadError && <p className="form-error">{loadError}</p>}
    </div>
  );
}
